import React, { useState, useEffect, useCallback } from 'react';
import { reportService } from '../../services/reportService';
import DateRangeFilter from './DateRangeFilter';
import { Download, AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine
} from 'recharts';
import SortableHeader, { useSort } from './SortableHeader';
import ChartContainer from './ChartContainer';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const EffectiveRateReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await reportService.getEffectiveRate(params);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching effective rate:', err);
      setError('Failed to load Effective Rate report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { sortConfig, requestSort, sortItems } = useSort('effective_hourly_rate', 'desc');

  const handleDateChange = ({ start_date, end_date }) => {
    setStartDate(start_date);
    setEndDate(end_date);
  };

  const handleExportCSV = () => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    reportService.downloadCSV(reportService.getEffectiveRate, params, 'effective_rate_report.csv');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);

  const chartData = items
    .filter(item => item.effective_hourly_rate != null)
    .slice(0, 15)
    .map(item => ({
      name: item.case_number,
      rate: parseFloat(item.effective_hourly_rate),
      hours: parseFloat(item.total_hours_worked),
    }));

  const avgRate = summary.avg_effective_rate ? parseFloat(summary.avg_effective_rate) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Effective Hourly Rate</h2>
        <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <DateRangeFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Overall Effective Rate</p>
          <p className="text-lg font-bold text-green-700">
            {summary.overall_effective_rate != null ? `${formatCurrency(summary.overall_effective_rate)}/hr` : 'N/A'}
          </p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Avg Rate per Case</p>
          <p className="text-lg font-bold text-blue-700">
            {summary.avg_effective_rate != null ? `${formatCurrency(summary.avg_effective_rate)}/hr` : 'N/A'}
          </p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Fees</p>
          <p className="text-lg font-bold text-yellow-700">{formatCurrency(summary.total_fees || 0)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Hours</p>
          <p className="text-lg font-bold text-gray-900">{parseFloat(summary.total_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Cases</p>
          <p className="text-lg font-bold text-gray-900">
            {summary.total_cases || 0}
            {summary.cases_with_zero_hours > 0 && (
              <span className="text-xs text-gray-400 ml-1">({summary.cases_with_zero_hours} no hours)</span>
            )}
          </p>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Effective Rate by Case</h3>
          <ChartContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis
                  tickFormatter={(v) => `$${v}`}
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  width={60}
                />
                <Tooltip
                  formatter={(value, name) => [
                    name === 'rate' ? `$${value.toFixed(2)}/hr` : `${value.toFixed(1)} hrs`,
                    name === 'rate' ? 'Effective Rate' : 'Hours'
                  ]}
                />
                {avgRate && (
                  <ReferenceLine y={avgRate} stroke="#CA8A04" strokeDasharray="5 5" label={{ value: `Avg: $${avgRate.toFixed(0)}/hr`, fill: '#CA8A04', fontSize: 11 }} />
                )}
                <Bar dataKey="rate" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
          </ChartContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Case" sortKey="case_number" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Client" sortKey="client_name" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Billing" sortKey="billing_type" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Fee" sortKey="fee_amount" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Hours" sortKey="total_hours_worked" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Eff. Rate" sortKey="effective_hourly_rate" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Collected" sortKey="amount_collected" sortConfig={sortConfig} onSort={requestSort} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedItems.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No flat-fee or contingency cases found</td></tr>
              ) : sortedItems.map((item) => (
                <tr key={item.case_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{item.case_number}</div>
                    <div className="text-xs text-gray-500">{item.case_title}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.client_name}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      item.billing_type === 'flat_fee' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {item.billing_type === 'flat_fee' ? 'Flat Fee' : 'Contingency'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.fee_amount)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{parseFloat(item.total_hours_worked).toFixed(1)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium">
                    {item.effective_hourly_rate != null ? (
                      <span className={parseFloat(item.effective_hourly_rate) >= (avgRate || 0) ? 'text-green-700' : 'text-red-700'}>
                        ${parseFloat(item.effective_hourly_rate).toFixed(2)}/hr
                      </span>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.amount_collected)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EffectiveRateReport;
